import { useState } from 'react';
import { api } from '../lib/api';
import { useSecurityConfig } from '../hooks/useSecurity';

interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'open' | 'void';
  description: string;
  created_at: string;
  pdf_url?: string;
}

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    period: 'mo',
    features: [
      'Up to 5 platforms',
      'Daily vulnerability scans',
      'SAST & DAST findings',
      'Dependency tracking',
      '7-day posture history',
      'Email support',
    ],
    pentest_credits: 0,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 149,
    period: 'mo',
    features: [
      'Up to 25 platforms',
      'Continuous scanning',
      'AI pentest engine (10 credits/mo)',
      'Advanced EPSS scoring',
      '90-day posture history',
      'KEV feed integration',
      'Team management',
      'Priority support',
    ],
    pentest_credits: 10,
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 499,
    period: 'mo',
    features: [
      'Unlimited platforms',
      'Real-time scanning',
      'AI pentest engine (100 credits/mo)',
      'Custom risk scoring',
      'Unlimited posture history',
      'Executive PDF reports',
      'SSO / SAML',
      'Dedicated support',
      'SLA guarantee',
    ],
    pentest_credits: 100,
  },
];

const STATUS_COLORS: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  open: 'bg-yellow-100 text-yellow-700',
  void: 'bg-gray-100 text-gray-500',
};

export default function BillingPage() {
  const { data: config, loading } = useSecurityConfig();
  const cfg = config as Record<string, unknown>;

  const currentTier = (cfg?.tier as string) ?? 'starter';
  const creditBalance = (cfg?.pentest_credits_remaining as number) ?? 0;
  const currentPeriodEnd = cfg?.current_period_end as string | undefined;
  const invoices = (cfg?.invoices as Invoice[]) ?? [];

  const [upgradeLoading, setUpgradeLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleUpgrade(tierId: string) {
    setError(null);
    setUpgradeLoading(tierId);
    try {
      const result = await api.post<{ checkout_url: string }>('/billing/checkout', { tier: tierId });
      window.location.href = result.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setUpgradeLoading(null);
    }
  }

  async function handlePortal() {
    setError(null);
    setPortalLoading(true);
    try {
      const result = await api.post<{ portal_url: string }>('/billing/portal');
      window.location.href = result.portal_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setPortalLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-32" />
          <div className="h-36 bg-gray-200 rounded" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const currentTierDef = TIERS.find(t => t.id === currentTier);

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Billing</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Current plan */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-1">Current Plan</h2>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-extrabold text-indigo-600 capitalize">{currentTierDef?.name ?? currentTier}</span>
              <span className="bg-indigo-100 text-indigo-700 text-sm font-semibold px-3 py-1 rounded-full capitalize">
                {currentTier}
              </span>
            </div>
            <p className="text-gray-500 mt-1 text-sm">
              ${currentTierDef?.price ?? 0}/month
              {currentPeriodEnd && (
                <span className="ml-2 text-gray-400">· Renews {new Date(currentPeriodEnd).toLocaleDateString()}</span>
              )}
            </p>
          </div>
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
          >
            {portalLoading ? (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : null}
            Manage Billing & Payment
          </button>
        </div>

        {/* Credit balance */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">AI Pentest Credits</p>
              <p className="text-xs text-gray-400 mt-0.5">Resets on billing cycle renewal</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-indigo-600">{creditBalance}</p>
              <p className="text-xs text-gray-400">of {currentTierDef?.pentest_credits ?? 0} remaining</p>
            </div>
          </div>
          <div className="mt-2 w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-indigo-500 rounded-full transition-all"
              style={{ width: `${currentTierDef?.pentest_credits ? (creditBalance / currentTierDef.pentest_credits) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Plan comparison */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-4">Upgrade Plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIERS.map(tier => {
            const isCurrent = tier.id === currentTier;
            const isHigher = TIERS.findIndex(t => t.id === tier.id) > TIERS.findIndex(t => t.id === currentTier);
            return (
              <div
                key={tier.id}
                className={`bg-white rounded-lg shadow p-6 relative border-2 ${
                  tier.highlight ? 'border-indigo-500' : isCurrent ? 'border-green-400' : 'border-transparent'
                }`}
              >
                {tier.highlight && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <span className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute top-3 right-3">
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Current</span>
                  </div>
                )}
                <h3 className="text-lg font-bold text-gray-900 mb-1">{tier.name}</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl font-extrabold text-gray-900">${tier.price}</span>
                  <span className="text-gray-400 text-sm ml-1">/{tier.period}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button disabled className="w-full py-2 px-4 rounded-md text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed">
                    Current Plan
                  </button>
                ) : isHigher ? (
                  <button
                    onClick={() => handleUpgrade(tier.id)}
                    disabled={upgradeLoading !== null}
                    className="w-full py-2 px-4 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {upgradeLoading === tier.id ? (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : null}
                    Upgrade to {tier.name}
                  </button>
                ) : (
                  <button
                    onClick={handlePortal}
                    className="w-full py-2 px-4 rounded-md text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    Downgrade
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoice history */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Invoice History</h2>
        </div>
        {invoices.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-400">No invoices yet.</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Date</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Description</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Amount</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-600">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{inv.description}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">
                    ${(inv.amount / 100).toFixed(2)} {inv.currency.toUpperCase()}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[inv.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {inv.pdf_url ? (
                      <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                        Download PDF
                      </a>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
